import { apiClient } from './apiClient';
import type { BudgetPacingStatus } from './googleAdsMonthlyService';

// MCC-level (cross-account) overview — Phase A.
// One card per account under the agency MCC. Metrics not yet computed backend
// side (AVG QS, wasted spend, pacing, sparkline) are absent here and rendered
// "not synced" by the UI — those are Phase B.

export type MccVerdict = 'ON_TRACK' | 'WATCH' | 'NO_VERDICT';

export interface MccAccountMetrics {
  spend_usd: number | null;
  conversions: number | null;
  cost_per_conversion_usd: number | null; // CPL
  conversion_rate: number | null; // CVR (fraction 0..1)
  ctr: number | null; // fraction 0..1
  cpc_usd: number | null;
  clicks: number | null;
  impressions: number | null;
}

export interface MccAccountCard {
  tenant_id: string;
  customer_id: string | null;
  product_tier: number | null;
  available: boolean;
  verdict: MccVerdict;
  metrics: MccAccountMetrics;
  previous: MccAccountMetrics | null;
  deltas: Record<string, unknown> | null;
  error: string | null;
}

export interface MccOverviewPeriod {
  current_start: string;
  current_end: string;
  previous_start: string;
  previous_end: string;
}

/**
 * ⛔ THESE THREE CALLS EXCEED THE DEFAULT 30s CLIENT TIMEOUT.
 *
 * Each fans out across every managed account under the MCC. Measured on
 * staging 2026-08-19 against 28 accounts: /mcc/overview took 36.0s with
 * active_only=true and 19.0s with active_only=false. The apiClient default is
 * 30s, so the browser aborted the request BEFORE the server answered, and the
 * page rendered a generic "flag and access" error. An operator lost time
 * checking a feature flag that was already on and a role that was already
 * correct.
 *
 * The fan-out is inherent to the view (it is a fleet-wide grid), so the honest
 * fix is to allow it the time it needs rather than pretend it is a fast call.
 * Same pattern as CAMPAIGN_SETUP_TIMEOUT_MS in googleAdsCampaignsService.
 *
 * ⚠ If this ever needs raising again, that is a signal to paginate the endpoint,
 * not to keep growing the number.
 */
const MCC_FLEET_TIMEOUT_MS = 120_000;

export interface MccOverviewResponse {
  generated_at: string;
  period: MccOverviewPeriod | null;
  active_only: boolean;
  account_count: number;
  /** Progressive loading: total targets in the fleet (all pages). */
  total_count?: number;
  offset?: number;
  limit?: number;
  accounts: MccAccountCard[];
}

export async function getMccOverview(params?: {
  activeOnly?: boolean;
  refresh?: boolean;
  limit?: number;
  offset?: number;
}): Promise<MccOverviewResponse> {
  const { data } = await apiClient.get<MccOverviewResponse>('/google-ads/mcc/overview', {
    params: {
      active_only: params?.activeOnly ?? false,
      refresh: params?.refresh ?? false,
      ...(params?.limit ? { limit: params.limit, offset: params.offset ?? 0 } : {}),
    },
    timeout: MCC_FLEET_TIMEOUT_MS,
  });
  return data;
}

// Pacing (Phase B) — operator-gated companion to the overview. Fetched in
// parallel and merged by tenant_id. Reuses the shared BudgetPacingStatus type.
export interface MccPacingCard {
  tenant_id: string;
  customer_id: string | null;
  pacing_status: BudgetPacingStatus;
  budget_usd: number | null;
  spent_usd: number | null;
  remaining_usd: number | null;
  days_left: number | null;
  ratio: number | null; // spent / budget
  error: string | null;
}

export interface MccPacingResponse {
  generated_at: string;
  account_count: number;
  accounts: MccPacingCard[];
}

export async function getMccPacing(params?: {
  refresh?: boolean;
}): Promise<MccPacingResponse> {
  const { data } = await apiClient.get<MccPacingResponse>('/google-ads/mcc/pacing', {
    params: { refresh: params?.refresh ?? false },
    timeout: MCC_FLEET_TIMEOUT_MS,
  });
  return data;
}

// Insights (Phase B) — Avg Quality Score + Wasted 7d. Heaviest MCC endpoint
// (keyword_view + search_term_view scans per account); fetched in parallel and
// merged by tenant_id to fill the two cells Phase A left "not synced".
export interface MccInsightsCard {
  tenant_id: string;
  customer_id: string | null;
  avg_quality_score: number | null; // null = not enough scored keywords
  scored_keyword_count: number;
  wasted_7d_usd: number | null;
  wasted_term_count: number;
  error: string | null;
}

export interface MccInsightsResponse {
  generated_at: string;
  account_count: number;
  accounts: MccInsightsCard[];
}

export async function getMccInsights(params?: {
  refresh?: boolean;
}): Promise<MccInsightsResponse> {
  const { data } = await apiClient.get<MccInsightsResponse>('/google-ads/mcc/insights', {
    params: { refresh: params?.refresh ?? false },
    timeout: MCC_FLEET_TIMEOUT_MS,
  });
  return data;
}
